/**
 * Turns live microphone audio into a stream of level readings — rms and peak in
 * dBFS, plus the silence classification — sampled four times a second.
 *
 * The caller owns the `AudioContext` and the graph. This module is handed an
 * `AnalyserNode` and never creates a context, so the measurement can share the
 * recording graph's context: a suspended context is itself one of the causes of
 * silence we are trying to tell apart, and a second context would start
 * suspended under the same autoplay rules.
 */

/** Peak indistinguishable from zeros: one 16-bit step already measures −90 dBFS. */
export const DIGITAL_SILENCE_PEAK_DBFS = -85;

/** Rms below this is near-silence — signal is present but unusable as speech. */
export const NEAR_SILENCE_RMS_DBFS = -60;

export const SAMPLE_INTERVAL_MS = 250;

/**
 * A duration as a count of samples, rounded up: a threshold has to cover the
 * whole duration rather than stop one sample short of it.
 */
export const samplesForMs = (ms: number): number =>
  Math.ceil(ms / SAMPLE_INTERVAL_MS);

/**
 * Stands in for `-Infinity` on a buffer of zeros, which JSON cannot encode. Below
 * the −91 dBFS of one 16-bit step, so zeros stay the lowest reading.
 */
export const SILENCE_FLOOR_DBFS = -100;

export type LevelSample = {
  rmsDb: number;
  peakDb: number;
  /** Peak below `DIGITAL_SILENCE_PEAK_DBFS`. */
  digitallySilent: boolean;
  /** Digitally silent, or rms below `NEAR_SILENCE_RMS_DBFS`. */
  silent: boolean;
};

export type LevelMeterSample = {
  /**
   * Tick number, counting from zero. Compare it against elapsed time to tell a
   * throttled reading from a silent one.
   */
  index: number;
  level: LevelSample;
};

export const amplitudeToDbfs = (amplitude: number): number =>
  amplitude <= 0
    ? SILENCE_FLOOR_DBFS
    : Math.max(SILENCE_FLOOR_DBFS, 20 * Math.log10(amplitude));

export const dbfsToAmplitude = (dbfs: number): number =>
  Math.pow(10, dbfs / 20);

/**
 * The silence classification for a pair of level readings. Shared by a single
 * 250 ms sample and by the aggregate of a window, so both answer "silent?" the
 * same way.
 */
export const classifyLevel = (
  rmsDb: number,
  peakDb: number
): Pick<LevelSample, "digitallySilent" | "silent"> => {
  const digitallySilent = peakDb < DIGITAL_SILENCE_PEAK_DBFS;
  return {
    digitallySilent,
    silent: digitallySilent || rmsDb < NEAR_SILENCE_RMS_DBFS,
  };
};

/** One reading, from the mean of the squared amplitudes and the peak in dBFS. */
const levelFromMeanSquare = (
  meanSquare: number,
  peakDb: number
): LevelSample => {
  const rmsDb = amplitudeToDbfs(Math.sqrt(meanSquare));
  return { rmsDb, peakDb, ...classifyLevel(rmsDb, peakDb) };
};

/**
 * One reading from many: the loudest peak, and the average power rather than the
 * average decibel. Decibels are logarithmic, so their mean pulls a stretch with
 * speech in it down towards the quiet parts.
 *
 * Requires at least one sample.
 */
export const combineLevelSamples = (
  samples: readonly LevelSample[]
): LevelSample => {
  const peakDb = Math.max(...samples.map((sample) => sample.peakDb));
  const meanSquare =
    samples.reduce(
      (total, sample) => total + dbfsToAmplitude(sample.rmsDb) ** 2,
      0
    ) / samples.length;

  return levelFromMeanSquare(meanSquare, peakDb);
};

/**
 * Float samples, not bytes: byte samples cannot separate exact zeros from a very
 * quiet microphone, and that distinction is the whole point of the measurement.
 */
export const readLevelSample = (
  analyser: AnalyserNode,
  buffer: Float32Array<ArrayBuffer>
): LevelSample => {
  analyser.getFloatTimeDomainData(buffer);

  // The analyser writes at most fftSize entries and ignores the rest, so a
  // longer buffer keeps a stale tail that must not reach the average.
  const sampleCount = Math.min(buffer.length, analyser.fftSize);

  let squareTotal = 0;
  let peak = 0;

  for (let i = 0; i < sampleCount; i += 1) {
    const sample = buffer[i]!;
    squareTotal += sample * sample;
    const magnitude = Math.abs(sample);
    if (magnitude > peak) peak = magnitude;
  }

  const meanSquare = sampleCount === 0 ? 0 : squareTotal / sampleCount;

  return levelFromMeanSquare(meanSquare, amplitudeToDbfs(peak));
};

/**
 * Samples one analyser four times a second.
 *
 * The first reading lands one interval in, not immediately: a freshly connected
 * analyser still holds zeros, which would read as digital silence.
 *
 * Returns a function that stops sampling. Call it when the consumer unmounts or
 * the stream it is measuring goes away.
 */
export const startLevelMeter = (
  analyser: AnalyserNode,
  onSample: (sample: LevelMeterSample) => void,
  /**
   * Called when the meter stops itself because the context closed. Without it a
   * consumer cannot tell a dead meter from a microphone that has gone quiet.
   */
  onContextClosed?: () => void
): (() => void) => {
  const buffer = new Float32Array(analyser.fftSize);

  let tickCount = 0;
  let intervalId = 0;
  const stop = () => window.clearInterval(intervalId);

  // setInterval, not requestAnimationFrame: RAF freezes in a hidden tab, and the
  // thresholds would read the gap as silence. Pages actively capturing media are
  // exempt from Chrome's aggressive timer throttling.
  intervalId = window.setInterval(() => {
    // A closed context keeps handing out zeros, which would read as silence. A
    // suspended one deliberately keeps being sampled: that is the reading which
    // separates our own audio-graph bug from a quiet room.
    if (analyser.context.state === "closed") {
      stop();
      onContextClosed?.();
      return;
    }

    const level = readLevelSample(analyser, buffer);

    // Counted before the call, so a throwing consumer cannot repeat an index.
    const index = tickCount;
    tickCount += 1;
    onSample({ index, level });
  }, SAMPLE_INTERVAL_MS);

  return stop;
};
